import { z } from 'zod';

/**
 * `'true'` / `'false'` → boolean. `z.coerce.boolean()` treats every non-empty
 * string as true, so setting a flag to "false" would have switched it on.
 */
const booleanFlag = (fallback: boolean) =>
  z
    .enum(['true', 'false'])
    .optional()
    .transform((value) => (value === undefined ? fallback : value === 'true'));

const envSchema = z.object({
  NEXT_PUBLIC_API_URL: z.string().url().default('http://localhost:3200/api'),
  NEXT_PUBLIC_WS_URL: z.string().url().default('ws://localhost:3200'),
  NEXT_PUBLIC_AUTH_COOKIE_NAME: z.string().default('auth_token'),
  NEXT_PUBLIC_AUTH_COOKIE_EXPIRES: z.coerce.number().default(7),
  NEXT_PUBLIC_ENABLE_ANALYTICS: booleanFlag(false),
  NEXT_PUBLIC_MAINTENANCE_MODE: booleanFlag(false),
  NEXT_PUBLIC_APP_NAME: z.string().default('Auction Chase'),
  NEXT_PUBLIC_APP_ENV: z
    .enum(['development', 'staging', 'production'])
    .default('development'),
  NEXT_PUBLIC_GOOGLE_CLIENT_ID: z
    .string()
    .default(
      '1091988956432-kkma84v26igm3cemtj711eslankdal6b.apps.googleusercontent.com'
    ),
  NEXT_PUBLIC_RECAPTCHA_SITE_KEY: z
    .string()
    .default('6LdzEI0rAAAAAENdnawrzULbSoxuIlLR1DWVKUZ8'),
  NEXT_PUBLIC_CHATWOOT_WEBSITE_TOKEN: z
    .string()
    .default('oNSExpVe1rwzzoXzSMMfqYoW'),
  NEXT_PUBLIC_CHATWOOT_BASE_URL: z
    .string()
    .url()
    .default('https://app.chatwoot.com'),
  NEXT_PUBLIC_CHATWOOT_ENABLED: booleanFlag(true),
  NEXT_PUBLIC_GOOGLE_ANALYTICS_ID: z.string().optional(),
  NEXT_PUBLIC_GOOGLE_ANALYTICS_ID_DEVELOPMENT: z.string().optional(),
  NEXT_PUBLIC_GOOGLE_ANALYTICS_ID_STAGING: z.string().optional(),
  NEXT_PUBLIC_GOOGLE_ANALYTICS_ID_PRODUCTION: z.string().optional(),
});

type EnvSource = Partial<Record<keyof z.input<typeof envSchema>, string>>;

/**
 * Production reports here when no GA variable is configured — Amplify sets
 * none today. Every other environment gets no GA unless it names its own
 * property, so local dev and staging stop writing into production analytics.
 */
const PRODUCTION_GOOGLE_ANALYTICS_ID = 'G-HC86D9N9PX';

export function parseClientEnv(source: EnvSource) {
  // An empty value (`NEXT_PUBLIC_X=`, as docs/DEVELOPMENT.md writes them) means
  // unset. Otherwise '' is not nullish and wins every `??` below.
  const definedOnly = Object.fromEntries(
    Object.entries(source).filter(([, value]) => value !== '')
  );
  // parse, not safeParse: values are inlined at build time, so a malformed
  // one fails the build instead of silently reverting production to defaults.
  const envVars = envSchema.parse(definedOnly);
  const appEnv = envVars.NEXT_PUBLIC_APP_ENV;

  const perEnvironmentGoogleAnalyticsId: Record<
    typeof appEnv,
    string | undefined
  > = {
    development: envVars.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID_DEVELOPMENT,
    staging: envVars.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID_STAGING,
    production: envVars.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID_PRODUCTION,
  };

  const googleAnalyticsId =
    perEnvironmentGoogleAnalyticsId[appEnv] ??
    envVars.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID ??
    (appEnv === 'production' ? PRODUCTION_GOOGLE_ANALYTICS_ID : null);

  return {
    api: {
      url: envVars.NEXT_PUBLIC_API_URL,
      wsUrl: envVars.NEXT_PUBLIC_WS_URL,
    },
    auth: {
      cookieName: envVars.NEXT_PUBLIC_AUTH_COOKIE_NAME,
      cookieExpires: envVars.NEXT_PUBLIC_AUTH_COOKIE_EXPIRES,
      googleClientId: envVars.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
    },
    recaptcha: {
      siteKey: envVars.NEXT_PUBLIC_RECAPTCHA_SITE_KEY,
    },
    support: {
      chatwootWebsiteToken: envVars.NEXT_PUBLIC_CHATWOOT_WEBSITE_TOKEN,
      chatwootBaseUrl: envVars.NEXT_PUBLIC_CHATWOOT_BASE_URL,
      chatwootEnabled: envVars.NEXT_PUBLIC_CHATWOOT_ENABLED,
    },
    features: {
      enableAnalytics: envVars.NEXT_PUBLIC_ENABLE_ANALYTICS,
      maintenanceMode: envVars.NEXT_PUBLIC_MAINTENANCE_MODE,
    },
    app: {
      name: envVars.NEXT_PUBLIC_APP_NAME,
      env: appEnv,
    },
    analytics: {
      googleAnalyticsId,
    },
  } as const;
}

/**
 * Each variable is read by its literal name. Next.js inlines only literal
 * `process.env.NEXT_PUBLIC_X` references into the browser bundle; passing the
 * whole `process.env` object left every field at its default in the browser.
 */
export const env = parseClientEnv({
  NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
  NEXT_PUBLIC_WS_URL: process.env.NEXT_PUBLIC_WS_URL,
  NEXT_PUBLIC_AUTH_COOKIE_NAME: process.env.NEXT_PUBLIC_AUTH_COOKIE_NAME,
  NEXT_PUBLIC_AUTH_COOKIE_EXPIRES: process.env.NEXT_PUBLIC_AUTH_COOKIE_EXPIRES,
  NEXT_PUBLIC_ENABLE_ANALYTICS: process.env.NEXT_PUBLIC_ENABLE_ANALYTICS,
  NEXT_PUBLIC_MAINTENANCE_MODE: process.env.NEXT_PUBLIC_MAINTENANCE_MODE,
  NEXT_PUBLIC_APP_NAME: process.env.NEXT_PUBLIC_APP_NAME,
  NEXT_PUBLIC_APP_ENV: process.env.NEXT_PUBLIC_APP_ENV,
  NEXT_PUBLIC_GOOGLE_CLIENT_ID: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
  NEXT_PUBLIC_RECAPTCHA_SITE_KEY: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY,
  NEXT_PUBLIC_CHATWOOT_WEBSITE_TOKEN:
    process.env.NEXT_PUBLIC_CHATWOOT_WEBSITE_TOKEN,
  NEXT_PUBLIC_CHATWOOT_BASE_URL: process.env.NEXT_PUBLIC_CHATWOOT_BASE_URL,
  NEXT_PUBLIC_CHATWOOT_ENABLED: process.env.NEXT_PUBLIC_CHATWOOT_ENABLED,
  NEXT_PUBLIC_GOOGLE_ANALYTICS_ID: process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID,
  NEXT_PUBLIC_GOOGLE_ANALYTICS_ID_DEVELOPMENT:
    process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID_DEVELOPMENT,
  NEXT_PUBLIC_GOOGLE_ANALYTICS_ID_STAGING:
    process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID_STAGING,
  NEXT_PUBLIC_GOOGLE_ANALYTICS_ID_PRODUCTION:
    process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID_PRODUCTION,
});

export const isProd = env.app.env === 'production';
